import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Avvvatars from "avvvatars-react";
import ClientAvatar from "@components/chat/ClientAvatar";
import Icon1 from "@assets/icons/Icon";
import { ordresApi } from "@services/api/ordres";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";
import ChatPanel from "@components/chat/ChatPanel";
import type { Ordre, OrdreStatus, OrdreTimelineEvent, OrdreDocument } from "types/ordre";
import VehicleImage from "@components/ui/VehicleImage";
import { DocumentPreviewModal } from "@components/ui/DocumentPreviewModal";
import DevisReminderModal from "@components/modals/DevisReminderModal";


// ─── Constants ───────────────────────────────────────────────

const STATUS_DISPLAY: Partial<Record<OrdreStatus, { label: string; bg: string; color: string }>> = {
  a_preparer:    { label: "en prépa",       bg: "#F4F4F4", color: "#A1A1A1" },
  devis_initial: { label: "devis initial",  bg: "#FEF3C7", color: "#D97706" },
  commande:      { label: "commandé",       bg: "#ECFFFE", color: "#47DDD5" },
  pret:          { label: "prêt",           bg: "#E8EAFF", color: "#5C6BFF" },
  reception:     { label: "réceptionné",   bg: "#F4F4F4", color: "#A1A1A1" },
  en_cours:      { label: "en cours",       bg: "#CACFFF", color: "#5C6BFF" },
  devis_supp:    { label: "devis supp.",    bg: "#FEF3C7", color: "#D97706" },
  accord:        { label: "accord",         bg: "#D1FAE5", color: "#059669" },
  acompte:       { label: "acompte",        bg: "#FEF3C7", color: "#D97706" },
  fin_de_travaux:{ label: "terminé",        bg: "#D1FAE5", color: "#059669" },
  facturation:   { label: "facturation",    bg: "#E8EAFF", color: "#6366f1" },
  solde:         { label: "solde",          bg: "#FEF3C7", color: "#D97706" },
  solde_paye:    { label: "solde payé",     bg: "#D1FAE5", color: "#059669" },
  disponible:    { label: "disponible",     bg: "#F1E7F9", color: "#8258FF" },
  restitue:      { label: "restitué",       bg: "#D1FAE5", color: "#059669" },
};

const MECHANIC_STATUSES: { value: OrdreStatus; label: string; bg: string; color: string }[] = [
  { value: "en_cours",       label: "En cours",            bg: "#CACFFF", color: "#5C6BFF" },
  { value: "devis_supp",     label: "Devis supp.",         bg: "#FEF3C7", color: "#D97706" },
  { value: "accord",         label: "Accord reçu",         bg: "#D1FAE5", color: "#059669" },
  { value: "acompte",        label: "Acompte",             bg: "#FEF3C7", color: "#D97706" },
  { value: "fin_de_travaux", label: "Fin de travaux",      bg: "#D1FAE5", color: "#059669" },
  { value: "disponible",     label: "Véhicule disponible", bg: "#F1E7F9", color: "#8258FF" },
];

const IMAGE_NAME_RE = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i;
const IMAGE_EXT_RE  = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i;

// ─── Helpers ─────────────────────────────────────────────────

function formatEventDate(iso: string): { text: string; color?: string } {
  const d = new Date(iso);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const eventDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

  if (eventDay.getTime() === today.getTime()) {
    return { text: `Auj. ${time}`, color: "#10b981" };
  }
  if (eventDay.getTime() === yesterday.getTime()) {
    return { text: `Hier ${time}`, color: "#171717" };
  }
  return { text: d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) + " " + time };
}

function isImageDoc(d: { type: string; name: string; gcs_path?: string }) {
  return d.type === "image" || IMAGE_EXT_RE.test(d.gcs_path || d.name);
}

// ─── Status bottom sheet ─────────────────────────────────────

function StatusBottomSheet({
  current,
  onSelect,
  onClose,
}: {
  current: OrdreStatus;
  onSelect: (s: OrdreStatus) => void;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex flex-col justify-end"
      style={{ background: "rgba(0,0,0,0.35)" }}
      onClick={onClose}
    >
      <div
        className="flex flex-col rounded-t-2xl"
        style={{ background: "#fff", padding: "20px 20px 36px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontWeight: 600, fontSize: 15, color: "#1E1E1E", marginBottom: 14, fontFamily: "Poppins" }}>
          Changer le statut
        </div>
        <div className="flex flex-col" style={{ gap: 8 }}>
          {MECHANIC_STATUSES.map((s) => (
            <button
              key={s.value}
              onClick={() => onSelect(s.value)}
              className="flex flex-row items-center justify-between"
              style={{
                padding: "12px 14px",
                borderRadius: 12,
                background: s.value === current ? s.bg : "#F9F9F9",
                border: s.value === current ? `1.5px solid ${s.color}33` : "1.5px solid transparent",
              }}
            >
              <span style={{ fontWeight: 500, fontSize: 14, color: s.value === current ? s.color : "#525252", fontFamily: "Poppins" }}>
                {s.label}
              </span>
              {s.value === current && (
                <Icon1 name="done" size={16} color={s.color} />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Timeline thumbnails (use pre-fetched URL map) ───────────

function MobileTimelineDocThumbnail({ url, onPreview }: { url: string | undefined; onPreview?: () => void }) {
  return (
    <div
      className="flex items-center justify-center flex-shrink-0 overflow-hidden"
      style={{ width: 64, height: 64, borderRadius: 8, background: "#F2F2F2", cursor: url ? "pointer" : "default" }}
      onClick={url ? onPreview : undefined}
    >
      {url ? (
        <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <Icon1 name="image" size={20} color="#D4D4D4" />
      )}
    </div>
  );
}

function MobileTimelineEvent({
  event,
  isLast,
  documents,
  urlMap,
  onPreview,
}: {
  event: OrdreTimelineEvent;
  isLast: boolean;
  documents?: OrdreDocument[];
  urlMap: Record<string, string>;
  onPreview?: (doc: { name: string; gcs_path?: string }, url: string) => void;
}) {
  const imageNames = event.type === "documents" && event.document_names
    ? event.document_names.filter((n) => IMAGE_NAME_RE.test(n))
    : [];
  const fileNames = event.type === "documents" && event.document_names
    ? event.document_names.filter((n) => !IMAGE_NAME_RE.test(n))
    : [];

  return (
    <div className="flex flex-row">
      {/* Date */}
      <div className="flex-shrink-0" style={{ width: 75, paddingTop: 2, textAlign: "right", paddingRight: 8 }}>
        <span style={{ fontWeight: 500, fontSize: 10, lineHeight: "8px", color: formatEventDate(event.date).color || "#A1A1A1" }}>
          {formatEventDate(event.date).text}
        </span>
      </div>

      {/* Dot + line */}
      <div className="flex flex-col items-center flex-shrink-0" style={{ width: 16 }}>
        <div className="rounded-full mt-1 flex-shrink-0" style={{ width: 8, height: 8, background: "#A1A1A1" }} />
        {!isLast && <div style={{ width: 1, background: "#A1A1A1" }} className="flex-1 my-0.5" />}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-1.5 ml-2 flex-1 pb-5" style={{ minWidth: 0 }}>
        <div className="flex flex-row gap-2 items-start">
          <div className="flex-shrink-0 overflow-hidden" style={{ borderRadius: 999 }}>
            {event.sender_type === "client" ? (
              <ClientAvatar size={28} />
            ) : (
              <Avvvatars value={event.user_name} style="shape" size={28} />
            )}
          </div>
          <div className="flex flex-col">
            <span style={{ fontWeight: 500, fontSize: 12, lineHeight: "90%", color: "#000000" }}>{event.user_name}</span>
            <span style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#A1A1A1" }}>{event.action}</span>
          </div>
        </div>

        {imageNames.length > 0 && documents && (
          <div className="flex flex-row flex-wrap" style={{ gap: 4, paddingLeft: 34 }}>
            {imageNames.map((name, i) => {
              const doc = documents.find((d) => d.name === name);
              const url = doc ? urlMap[doc.id] : undefined;
              return (
                <MobileTimelineDocThumbnail
                  key={i}
                  url={url}
                  onPreview={url && doc ? () => onPreview?.({ name: doc.name, gcs_path: doc.gcs_path }, url) : undefined}
                />
              );
            })}
          </div>
        )}

        {fileNames.length > 0 && (
          <div className="flex flex-col gap-0.5" style={{ paddingLeft: 34 }}>
            {fileNames.map((name, i) => (
              <div key={i} className="flex flex-row items-center gap-2">
                <Icon1 name="file" size={11} color="#A1A1A1" />
                <span style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#A1A1A1" }}>{name}</span>
              </div>
            ))}
          </div>
        )}

        {event.type === "photos" && event.photo_urls && (
          <div className="flex flex-row flex-wrap" style={{ gap: 4, paddingLeft: 34 }}>
            {event.photo_urls.map((url, i) => (
              <div
                key={i}
                className="flex-shrink-0 overflow-hidden"
                style={{ width: 64, height: 64, borderRadius: 8, background: "#F2F2F2", cursor: "pointer" }}
                onClick={() => onPreview?.({ name: `Photo ${i + 1}`, gcs_path: url }, url)}
              >
                <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        )}

        {event.type === "status_change" && event.status_label && (
          <div style={{ paddingLeft: 34 }}>
            <div
              className="inline-flex flex-row items-center"
              style={{ padding: "2px 6px", gap: 6, background: "#ECFFFE", borderRadius: 6 }}
            >
              <span style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#47DDD5" }}>
                {event.status_label}
              </span>
              <Icon1 name="done" size={13} color="#47DDD5" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Photos tab ──────────────────────────────────────────────

function MobilePhotosTab({
  documents,
  urlMap,
  uploadedPreviews,
  onPreview,
}: {
  documents: OrdreDocument[];
  urlMap: Record<string, string>;
  uploadedPreviews: string[];
  onPreview?: (doc: { name: string; gcs_path?: string }, url: string) => void;
}) {
  const photos = documents.filter(isImageDoc);

  if (photos.length === 0 && uploadedPreviews.length === 0) {
    return (
      <div className="flex items-center justify-center py-10">
        <span style={{ fontSize: 13, color: "#A8A8A8" }}>Aucune photo</span>
      </div>
    );
  }

  return (
    <div className="flex flex-row flex-wrap" style={{ gap: 6 }}>
      {photos.map((p) => (
        <div
          key={p.id}
          className="flex items-center justify-center flex-shrink-0 overflow-hidden"
          style={{ width: 100, height: 100, borderRadius: 10, background: "#F2F2F2", cursor: urlMap[p.id] ? "pointer" : "default" }}
          onClick={urlMap[p.id] ? () => onPreview?.({ name: p.name, gcs_path: p.gcs_path }, urlMap[p.id]) : undefined}
        >
          {urlMap[p.id] ? (
            <img src={urlMap[p.id]} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <Icon1 name="image" size={24} color="#D4D4D4" />
          )}
        </div>
      ))}
      {uploadedPreviews.map((url, i) => (
        <div
          key={`preview-${i}`}
          className="flex-shrink-0 overflow-hidden"
          style={{ width: 100, height: 100, borderRadius: 10, opacity: 0.6 }}
        >
          <img src={url} alt="" className="w-full h-full object-cover" />
        </div>
      ))}
    </div>
  );
}

// ─── Note tab ────────────────────────────────────────────────

function MobileNoteTab({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Ajouter une note sur cet ordre..."
      style={{
        width: "100%",
        minHeight: 180,
        border: "1px solid #E5E5E5",
        borderRadius: 12,
        padding: "12px 14px",
        fontSize: 14,
        fontFamily: "Poppins",
        color: "#1E1E1E",
        resize: "none",
        outline: "none",
        background: "#FAFAFA",
      }}
    />
  );
}

// ─── Main Component ──────────────────────────────────────────

export default function MobileOrdreDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userData = useDataStore((s) => s.userData);
  const orgData = useDataStore((s) => s.orgData);

  const [ordre, setOrdre] = useState<Ordre | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"fil" | "photos" | "chat" | "note">("fil");

  // URL map pre-fetched for all documents
  const [urlMap, setUrlMap] = useState<Record<string, string>>({});

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [uploadedPreviews, setUploadedPreviews] = useState<string[]>([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // Preview state
  const [previewDoc, setPreviewDoc] = useState<{ doc: { name: string; gcs_path?: string }; url: string } | null>(null);

  // Status state
  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Reminder modal
  const [showReminderModal, setShowReminderModal] = useState(false);

  // Note state
  const [noteValue, setNoteValue] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  // Load ordre
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    ordresApi
      .get(id)
      .then((o) => {
        setOrdre(o);
        setNoteValue(o.notes || "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  // Pre-fetch all document URLs in parallel
  useEffect(() => {
    if (!ordre || !id) return;
    const docs = ordre.documents || [];
    if (!docs.length) return;
    Promise.all(
      docs.map(async (doc) => {
        try {
          const url = await ordresApi.getDocumentUrl(id, doc.id);
          return [doc.id, url] as [string, string];
        } catch {
          return [doc.id, ""] as [string, string];
        }
      })
    ).then((entries) => {
      setUrlMap(Object.fromEntries(entries.filter(([, url]) => url)));
    });
  }, [ordre?.id, (ordre?.documents || []).length]);

  const handleUpload = async (files: FileList) => {
    if (!ordre || !id) return;
    // Convert to array immediately — iOS clears FileList when input.value is reset
    const fileArray = Array.from(files);
    if (!fileArray.length) return;
    setUploading(true);
    setUploadSuccess(false);

    const { compressImage } = await import("@utils/compressImage");
    const compressed = await Promise.all(fileArray.map((f) => compressImage(f)));

    // Show local previews immediately
    const previews: string[] = [];
    for (const file of compressed) {
      if (file.type.startsWith("image/")) previews.push(URL.createObjectURL(file));
    }
    setUploadedPreviews((prev) => [...prev, ...previews]);
    setUploadProgress({ done: 0, total: compressed.length });

    try {
      let updated: Ordre | null = null;
      for (let i = 0; i < compressed.length; i++) {
        const file = compressed[i];
        const docType = file.type.startsWith("image/") ? "photo" : undefined;
        updated = await ordresApi.uploadDocument(id, file, undefined, docType);
        setUploadProgress({ done: i + 1, total: compressed.length });
      }
      if (updated) {
        setOrdre(updated);
        setNoteValue(updated.notes || "");
      }
      setUploadSuccess(true);
      setUploadedPreviews([]);
      setTimeout(() => setUploadSuccess(false), 3000);
    } catch {
      showNotification({ type: "error", message: "Échec de l'envoi des photos" });
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleStatusChange = async (newStatus: OrdreStatus) => {
    if (!id) return;
    setShowStatusPicker(false);
    setUpdatingStatus(true);
    try {
      const updated = await ordresApi.update(id, { status: newStatus });
      setOrdre(updated);
    } catch {
      showNotification({ type: "error", message: "Impossible de changer le statut" });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleNoteSave = async () => {
    if (!id) return;
    setSavingNote(true);
    try {
      const updated = await ordresApi.update(id, { notes: noteValue });
      setOrdre(updated);
      showNotification({ type: "success", message: "Note enregistrée" });
    } catch {
      showNotification({ type: "error", message: "Impossible d'enregistrer la note" });
    } finally {
      setSavingNote(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-screen bg-white" style={{ fontFamily: "Poppins" }}>
        {/* Header skeleton */}
        <div className="flex flex-row items-center" style={{ padding: "30px 20px 0", gap: 12 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: "#EEEEEE" }} className="animate-pulse" />
          <div style={{ width: 140, height: 14, borderRadius: 6, background: "#EEEEEE" }} className="animate-pulse" />
        </div>
        {/* Card skeleton */}
        <div style={{ padding: "20px 20px 0" }}>
          <div className="flex flex-row items-center" style={{ padding: 4, gap: 10, background: "#F9F9F9", borderRadius: 10, height: 76 }}>
            <div style={{ width: 69, height: 64, borderRadius: 10, background: "#EEEEEE" }} className="animate-pulse" />
            <div className="flex flex-col gap-2 flex-1">
              <div style={{ height: 12, background: "#EEEEEE", borderRadius: 4 }} className="animate-pulse" />
              <div style={{ height: 10, width: "60%", background: "#EEEEEE", borderRadius: 4 }} className="animate-pulse" />
            </div>
          </div>
        </div>
        {/* Timeline skeleton */}
        <div style={{ padding: "30px 20px 0", display: "flex", flexDirection: "column", gap: 20 }}>
          {[80, 60, 90].map((w, i) => (
            <div key={i} className="flex flex-row" style={{ gap: 10 }}>
              <div style={{ width: 75, height: 10, borderRadius: 4, background: "#EEEEEE" }} className="animate-pulse" />
              <div style={{ width: 8, height: 8, borderRadius: 999, background: "#EEEEEE", marginTop: 2, flexShrink: 0 }} className="animate-pulse" />
              <div style={{ flex: 1, height: 10, borderRadius: 4, background: "#EEEEEE" }} className="animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!ordre) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-white gap-4">
        <span style={{ fontFamily: "Poppins", fontSize: 14, color: "#A8A8A8" }}>Ordre introuvable</span>
        <button
          onClick={() => navigate("/m")}
          style={{ fontFamily: "Poppins", fontSize: 14, color: "#5C6BFF" }}
        >
          Retour aux ordres
        </button>
      </div>
    );
  }

  const status = STATUS_DISPLAY[ordre.status];
  const vehicle = ordre.vehicle;
  const hasVehicle = !!vehicle;
  const timeline = ordre.timeline || [];
  const sorted = [...timeline].sort((a, b) => (a.date < b.date ? 1 : -1));
  const noteChanged = noteValue !== (ordre.notes || "");

  return (
    <div className="flex flex-col bg-white" style={{ fontFamily: "Poppins", height: "100dvh" }}>
      {/* Header with back button */}
      <div className="flex flex-row items-center" style={{ padding: "max(30px, env(safe-area-inset-top, 30px)) 20px 0", gap: 8 }}>
        <button
          onClick={() => navigate("/m")}
          className="flex items-center justify-center flex-shrink-0"
          style={{ width: 32, height: 32, borderRadius: 8, background: "#F4F4F4" }}
        >
          <Icon1 name="back" size={16} color="#1E1E1E" />
        </button>
        <span className="truncate" style={{ fontWeight: 600, fontSize: 16, color: "#1E1E1E", flex: 1 }}>
          {ordre.name}
        </span>
        {ordre?.intervention_groups?.initial?.devis_token && (
          <button
            onClick={() => setShowReminderModal(true)}
            className="flex items-center justify-center flex-shrink-0"
            style={{ width: 32, height: 32, borderRadius: 8, background: "#F4F4F4" }}
          >
            <Icon1 name="cloche" size={16} color="#1E1E1E" />
          </button>
        )}
      </div>

      {/* Order card (display only, status tappable) */}
      <div style={{ padding: "12px 20px 0" }}>
        <div
          className="flex flex-row items-stretch"
          style={{ padding: 4, gap: 10, background: "#FFFFFF", boxShadow: "0px 2px 6.3px rgba(0,0,0,0.10)", borderRadius: 10 }}
        >
          {/* Vehicle thumbnail */}
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{ width: 69, height: 64, background: "#F2F2F2", borderRadius: 10, overflow: "hidden" }}
          >
            {hasVehicle ? (
              <VehicleImage marque={vehicle!.marque} modele={vehicle!.modele} coloris={vehicle!.coloris} mec={vehicle!.mec} className="w-[59px]" iconSize={24} />
            ) : (
              <Icon1 name="car" size={24} color="#A1A1A1" />
            )}
          </div>

          {/* Info */}
          <div className="flex flex-col justify-center flex-1" style={{ padding: "4px 0", gap: 3, minWidth: 0 }}>
            <div className="flex flex-row items-center gap-1.5 flex-wrap">
              <span className="truncate" style={{ fontWeight: 500, fontSize: 12, lineHeight: "18px", color: "#1E1E1E" }}>
                {ordre.num}
              </span>
              {ordre.assigned_to && (
                <span style={{ fontWeight: 400, fontSize: 10, color: "#A8A8A8" }}>— {ordre.assigned_to}</span>
              )}
            </div>
            <span className="truncate" style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#A8A8A8" }}>
              {ordre.immatriculation || vehicle?.plate || "—"}
            </span>
          </div>

          {/* Status — tappable */}
          <button
            onClick={() => setShowStatusPicker(true)}
            disabled={updatingStatus}
            className="flex items-center justify-center flex-shrink-0 self-center"
            style={{
              padding: "5px 10px",
              background: status?.bg || "#F4F4F4",
              borderRadius: 8,
              opacity: updatingStatus ? 0.6 : 1,
            }}
          >
            <span style={{ fontWeight: 500, fontSize: 12, color: status?.color || "#A1A1A1" }}>
              {updatingStatus ? "..." : (status?.label || ordre.status)}
            </span>
          </button>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex flex-row" style={{ margin: "16px 20px 0", borderBottom: "1px solid #E5E5E5" }}>
        {([["fil", "Fil"], ["photos", "Photos"], ["chat", "Chat"], ["note", "Note"]] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            style={{
              flex: 1,
              padding: "8px 0",
              fontWeight: 500,
              fontSize: 12,
              color: activeTab === key ? "#1E1E1E" : "#A8A8A8",
              borderBottom: activeTab === key ? "2px solid #1E1E1E" : "2px solid transparent",
              background: "none",
            }}
          >
            {label}
            {key === "note" && noteChanged && (
              <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: 3, background: "#6366F1", marginLeft: 4, verticalAlign: "middle" }} />
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto" style={{ padding: "16px 20px 10px" }}>
        {activeTab === "fil" && (
          sorted.length === 0 ? (
            <div className="flex items-center justify-center py-10">
              <span style={{ fontSize: 13, color: "#A8A8A8" }}>Aucun événement</span>
            </div>
          ) : (
            <div className="flex flex-col">
              {sorted.map((ev, i) => (
                <MobileTimelineEvent
                  key={ev.id}
                  event={ev}
                  isLast={i === sorted.length - 1}
                  documents={ordre.documents}
                  urlMap={urlMap}
                  onPreview={(doc, url) => setPreviewDoc({ doc, url })}
                />
              ))}
            </div>
          )
        )}
        {activeTab === "photos" && (
          <MobilePhotosTab
            documents={ordre.documents || []}
            urlMap={urlMap}
            uploadedPreviews={uploadedPreviews}
            onPreview={(doc, url) => setPreviewDoc({ doc, url })}
          />
        )}
        {activeTab === "chat" && (
          <ChatPanel
            discussionId={ordre.discussion_id}
            internalDiscussionId={ordre.internal_discussion_id}
            entityType="ordre"
            entityId={ordre.id}
            entityName={ordre.name}
            compact
            whatsappLinked={!!(orgData?.whatsapp?.connected && ordre.contacts?.[0]?.phone)}
            whatsappProvider={orgData?.whatsapp?.provider || "wasender"}
          />
        )}
        {activeTab === "note" && (
          <MobileNoteTab value={noteValue} onChange={setNoteValue} />
        )}
      </div>

      {/* Bottom action area */}
      {activeTab === "note" ? (
        <div style={{ padding: "10px 20px" }}>
          <button
            onClick={handleNoteSave}
            disabled={savingNote || !noteChanged}
            className="w-full flex items-center justify-center"
            style={{
              height: 50,
              borderRadius: 30,
              background: noteChanged ? "#1E1E1E" : "#E4E4E4",
            }}
          >
            <span style={{ fontWeight: 500, fontSize: 14, color: noteChanged ? "#fff" : "#A1A1A1" }}>
              {savingNote ? "Enregistrement..." : "Enregistrer la note"}
            </span>
          </button>
        </div>
      ) : activeTab === "chat" ? null : (
        <div style={{ padding: "8px 20px 10px", display: "flex", flexDirection: "column", gap: 6 }}>
          {/* Progress / success feedback */}
          {uploadProgress && (
            <div className="flex items-center justify-center">
              <span style={{ fontSize: 12, color: "#A8A8A8", fontFamily: "Poppins" }}>
                Envoi {uploadProgress.done}/{uploadProgress.total}...
              </span>
            </div>
          )}
          {uploadSuccess && (
            <div className="flex items-center justify-center">
              <span style={{ fontSize: 12, color: "#10b981", fontFamily: "Poppins", fontWeight: 500 }}>
                Photos ajoutées
              </span>
            </div>
          )}

          {/* Camera + Gallery buttons */}
          <div className="flex flex-row" style={{ gap: 8 }}>
            <button
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center justify-center flex-1"
              style={{ height: 48, borderRadius: 30, background: uploading ? "#D4D4D4" : "#1E1E1E", gap: 8 }}
            >
              <Icon1 name="photo" size={16} color={uploading ? "#A1A1A1" : "#fff"} />
              <span style={{ fontWeight: 500, fontSize: 13, color: uploading ? "#A1A1A1" : "#fff" }}>
                Appareil photo
              </span>
            </button>
            <button
              onClick={() => galleryInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center justify-center flex-1"
              style={{ height: 48, borderRadius: 30, background: uploading ? "#D4D4D4" : "#E4E4E4", gap: 8 }}
            >
              <Icon1 name="image" size={16} color={uploading ? "#A1A1A1" : "#636363"} />
              <span style={{ fontWeight: 500, fontSize: 13, color: uploading ? "#A1A1A1" : "#636363" }}>
                Galerie
              </span>
            </button>
          </div>

          {/* Camera input (forces camera on mobile) */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => { if (e.target.files?.length) handleUpload(e.target.files); e.target.value = ""; }}
          />
          {/* Gallery input (shows file picker / gallery) */}
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            className="hidden"
            onChange={(e) => { if (e.target.files?.length) handleUpload(e.target.files); e.target.value = ""; }}
          />
        </div>
      )}

      {/* Bottom indicator */}
      <div className="flex justify-center" style={{ paddingBottom: "max(8px, env(safe-area-inset-bottom, 8px))" }}>
        <div style={{ width: 104, height: 5, background: "#000000", borderRadius: 30 }} />
      </div>

      {/* Status picker bottom sheet */}
      {showStatusPicker && (
        <StatusBottomSheet
          current={ordre.status}
          onSelect={handleStatusChange}
          onClose={() => setShowStatusPicker(false)}
        />
      )}

      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc.doc}
          url={previewDoc.url}
          onClose={() => setPreviewDoc(null)}
          onDownload={() => window.open(previewDoc.url, "_blank")}
        />
      )}

      {showReminderModal && ordre?.intervention_groups?.initial?.devis_token && (
        <DevisReminderModal
          onClose={() => setShowReminderModal(false)}
          tokenId={ordre.intervention_groups.initial.devis_token}
        />
      )}
    </div>
  );
}
